import React, { useEffect, useMemo, useState } from "react";
import {
  Layout,
  Card,
  Button,
  Select,
  DatePicker,
  List,
  Tag,
  Space,
  Popconfirm,
  Empty,
  Modal,
  Input,
  Divider,
  Tabs
} from "antd";
import {
  DeleteOutlined,
  SaveOutlined,
  ClearOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  TeamOutlined,
  PlusOutlined,
  EditOutlined
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE } from "./api";

const { Header, Content } = Layout;
const { Option } = Select;
const { Search } = Input;

const SCROLL_WRAP_STYLE = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  paddingRight: 14,
  paddingBottom: 24,
  boxSizing: "border-box",
  overscrollBehavior: "contain",
  scrollbarGutter: "stable both-edges" // Chrome/Edge reserving space for scrollbar
};

// --- helpers for id/guid safety ---
const toKey = (v) => (v == null ? "" : String(v).trim().toLowerCase());
const sameEmployee = (emp, guid) => {
  const k = toKey(guid);
  return toKey(emp?.id) === k || toKey(emp?.guid) === k;
};

export default function ProductionShiftPlanningByDay() {
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);

  const [machineEmployees, setMachineEmployees] = useState([]); // [{id,guid?,name,maxMinutes?}]
  const [machineOrders, setMachineOrders] = useState([]); // [{id,name,duration}]
  const [completedOrders, setCompletedOrders] = useState([]); // виконані наряди (історія)
  const [ordersTab, setOrdersTab] = useState("available"); // available | completed
  const [machineSchedule, setMachineSchedule] = useState([]); // [{from:"07:00", to:"11:00"}, ...]
  const [nowTick, setNowTick] = useState(dayjs()); // для перерахунку доступного часу в реальному часі
  const [selectedDate, setSelectedDate] = useState(dayjs());

  // plannedOrders[dateKey][machineId] = { employees: [...ids], orders: [...] }
  const [plannedOrders, setPlannedOrders] = useState({});

  // Groups
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsLoadedOnce, setGroupsLoadedOnce] = useState(false);
  const [groupSelectGuid, setGroupSelectGuid] = useState(null);

  // Modal CRUD only
  const [groupsModalOpen, setGroupsModalOpen] = useState(false);
  const [groupSearch, setGroupSearch] = useState("");
  const [activeGroupGuid, setActiveGroupGuid] = useState(null);
  const [groupForm, setGroupForm] = useState({ guid: null, name: "", employeeIds: [] });
  const [groupsCrudLoading, setGroupsCrudLoading] = useState(false);

  // Machines
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/unf_new/hs/exchange?model=MachinesListPlan`);
        if (!res.ok) throw new Error("Ошибка при загрузке станков");
        const data = await res.json();
        setMachines(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error("Не удалось загрузить станки: " + err.message);
      }
    })();
  }, []);

  const dateKey = selectedDate.format("YYYY-MM-DD");
  const dayObj = plannedOrders[dateKey]?.[selectedMachine] || { employees: [], orders: [] };
  const selectedEmpsIds = Array.isArray(dayObj.employees) ? dayObj.employees : [];
  const plannedList = Array.isArray(dayObj.orders) ? dayObj.orders : [];

  const currentMachine = useMemo(
    () => machines.find((m) => m.id === selectedMachine) || null,
    [machines, selectedMachine]
  );
  const showOrdersSelect = !!currentMachine?.ShowOrdersSelect;

  // Employees by machine (normalize guid -> id)
  useEffect(() => {
    if (!selectedMachine) {
      setMachineEmployees([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/unf_new/hs/exchange?model=UsersByMachinePlan&machineGuid=${selectedMachine}`
        );
        if (!res.ok) throw new Error("Ошибка при загрузке сотрудников");
        const employees = await res.json();
        const normalized = (Array.isArray(employees) ? employees : []).map((e) => ({
          ...e,
          id: e.id ?? e.guid ?? null, // основной ключ в UI
          guid: e.guid ?? e.id ?? null, // оставляем и guid для подстраховки
          name: e.name ?? e.fullName ?? e.FIO ?? ""
        }));
        setMachineEmployees(normalized);
      } catch (err) {
        toast.error("Не удалось загрузить сотрудников: " + err.message);
      }
    })();
  }, [selectedMachine]);

  // Orders (optional)
  useEffect(() => {
    if (!selectedMachine || !showOrdersSelect) {
      setMachineOrders([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/unf_new/hs/exchange?model=OrdersByMachinePlan&machineGuid=${selectedMachine}&dateKey=${dateKey}`
        );
        if (!res.ok) throw new Error("Ошибка при загрузке нарядов");
        const ordersData = await res.json();
        setMachineOrders(Array.isArray(ordersData) ? ordersData : []);
      } catch (err) {
        toast.error("Не удалось загрузить наряды: " + err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachine, showOrdersSelect, dateKey]);

  // Виконані наряди (історія) — вантажимо коли перемкнулись на вкладку
  useEffect(() => {
    if (!selectedMachine || !showOrdersSelect || ordersTab !== "completed") {
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/unf_new/hs/exchange?model=CompletedOrdersByMachine&machineGuid=${selectedMachine}`
        );
        if (!res.ok) throw new Error("Помилка при завантаженні виконаних нарядів");
        const data = await res.json();
        setCompletedOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error("Не вдалося завантажити виконані наряди: " + err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachine, showOrdersSelect, ordersTab]);

  // Графік верстата — для розрахунку доступного часу
  useEffect(() => {
    if (!selectedMachine) {
      setMachineSchedule([]);
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/unf_new/hs/exchange?model=MachineSchedule&machineGuid=${selectedMachine}&date=${dateKey}`
        );
        if (!res.ok) throw new Error("Помилка при завантаженні графіка");
        const data = await res.json();
        setMachineSchedule(Array.isArray(data?.intervals) ? data.intervals : []);
      } catch (err) {
        // графік не критичний — просто не покажемо доступний час
        setMachineSchedule([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachine, dateKey]);

  // Тікер — перераховуємо доступний час щохвилини
  useEffect(() => {
    const timer = setInterval(() => setNowTick(dayjs()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Planned
  useEffect(() => {
    if (!selectedMachine || !selectedDate) return;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/unf_new/hs/exchange?model=PlannedOrders&machineGuid=${selectedMachine}&dateKey=${dateKey}`
        );
        if (!res.ok) throw new Error("Ошибка при загрузке планов");
        const data = await res.json();
        const normalized = {
          employees: Array.isArray(data?.employees) ? data.employees : [],
          orders: Array.isArray(data?.orders) ? data.orders : []
        };
        setPlannedOrders((prev) => ({
          ...prev,
          [dateKey]: {
            ...(prev[dateKey] || {}),
            [selectedMachine]: normalized
          }
        }));
      } catch (err) {
        toast.error("Не удалось загрузить планирование: " + err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachine, selectedDate]);

  // Autosave
  useEffect(() => {
    if (!Object.keys(plannedOrders).length) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/unf_new/hs/exchange?model=SavePlannedOrders`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(plannedOrders)
        });
        if (!res.ok) throw new Error("Ошибка при сохранении плана");
      } catch (err) {
        toast.error("Не удалось сохранить план: " + err.message);
      }
    })();
  }, [plannedOrders]);

  // Helpers
  const empById = useMemo(
    () => Object.fromEntries(machineEmployees.map((e) => [String(e.id), e])),
    [machineEmployees]
  );

  // Робочий час станка від графіка та поточного моменту.
  // Майбутній день — повний робочий час дня; сьогодні — від поточного моменту
  // (враховуючи що частина інтервалів уже минула); минулий день — 0.
  const scheduleMinutes = useMemo(() => {
    if (!Array.isArray(machineSchedule) || machineSchedule.length === 0) return 0;

    const toMin = (hhmm) => {
      const [h, m] = String(hhmm).split(":").map((x) => parseInt(x, 10));
      return (h || 0) * 60 + (m || 0);
    };

    const isFuture = selectedDate.isAfter(nowTick, "day");
    const isPast = selectedDate.isBefore(nowTick, "day");

    if (isPast) return 0;

    const nowMinutes = nowTick.hour() * 60 + nowTick.minute();

    let total = 0;
    for (const interval of machineSchedule) {
      const from = toMin(interval.from);
      const to = toMin(interval.to);
      if (to <= from) continue;

      // майбутня дата — весь інтервал; сьогодні — від поточного моменту
      const effectiveFrom = isFuture ? from : Math.max(from, nowMinutes);
      if (effectiveFrom < to) {
        total += to - effectiveFrom;
      }
    }
    return total;
  }, [machineSchedule, selectedDate, nowTick]);

  // Заплановано — сума операцій, які ще НЕ брались в роботу (не почались і не завершені)
  const sumPlanned = plannedList.reduce(
    (acc, o) => acc + (!o?.started && !o?.done ? o?.duration || 0 : 0),
    0
  );

  // Доступно = графік станка − усі незавершені наряди в плані (логіка без змін)
  const sumUnfinished = plannedList.reduce((acc, o) => acc + (o?.done ? 0 : o?.duration || 0), 0);
  const availableMinutes = scheduleMinutes - sumUnfinished;

  const updatePlannedEmployees = (newEmployees) => {
    setPlannedOrders((prev) => {
      const oldDateObj = prev[dateKey] || {};
      const oldMachineObj = oldDateObj[selectedMachine] || { employees: [], orders: [] };
      return {
        ...prev,
        [dateKey]: {
          ...oldDateObj,
          [selectedMachine]: {
            orders: Array.isArray(oldMachineObj.orders) ? oldMachineObj.orders : [],
            employees: newEmployees
          }
        }
      };
    });
  };

  const updatePlannedOrders = (newOrders) => {
    setPlannedOrders((prev) => {
      const oldDateObj = prev[dateKey] || {};
      const oldMachineObj = oldDateObj[selectedMachine] || { employees: [], orders: [] };
      return {
        ...prev,
        [dateKey]: {
          ...oldDateObj,
          [selectedMachine]: {
            employees: Array.isArray(oldMachineObj.employees) ? oldMachineObj.employees : [],
            orders: newOrders
          }
        }
      };
    });
  };

  const addEmployee = (empId) => {
    const key = String(empId);
    if (selectedEmpsIds.map(String).includes(key)) return;
    updatePlannedEmployees([...selectedEmpsIds, empId]);
  };
  const removeEmployee = (empId) => {
    const key = String(empId);
    updatePlannedEmployees(selectedEmpsIds.filter((id) => String(id) !== key));
  };
  const clearAllEmployees = () => updatePlannedEmployees([]);

  // Orders
  const handleAddOrder = (order) => {
    if (!selectedMachine) {
      toast.error("Оберіть верстат перед плануванням!");
      return;
    }
    if (plannedList.some((o) => o.id === order.id)) {
      toast.warn("Цей наряд вже додано у план!");
      return;
    }
    updatePlannedOrders([...plannedList, order]);
    toast.success("Наряд додано у план");
  };
  const handleRemoveOrder = (orderId) => {
    updatePlannedOrders(plannedList.filter((o) => o.id !== orderId));
  };
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = [...plannedList];
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    updatePlannedOrders(reordered);
  };

  // Рендер одного наряду у списку (спільний для вкладок "Доступні" та "Виконані")
  const renderOrderItem = (order) => {
    const isChosen = plannedList.some((o) => o.id === order.id);
    return (
      <List.Item
        style={{ backgroundColor: isChosen ? "#fffbe6" : "inherit", borderRadius: 8 }}
        actions={[
          <Button
            key="addOrder"
            size="small"
            type="primary"
            ghost
            disabled={isChosen}
            onClick={() => handleAddOrder(order)}
          >
            Додати
          </Button>
        ]}
      >
        <Space direction="vertical" size={2}>
          <span>
            {order.name} ({order.duration} хв)
          </span>
          <Space size={4} wrap>
            {order.done && <Tag color="green">Виконано</Tag>}
            {!order.done && order.started && <Tag color="orange">В роботі</Tag>}
            {order.factTime > 0 && order.normTime > 0 && (
              <Tag color="gold">
                залишок {order.normTime - order.factTime} з {order.normTime} хв
              </Tag>
            )}
            {order.factTime > 0 && !(order.normTime > 0) && (
              <Tag color="gold">факт {order.factTime} хв</Tag>
            )}
            {Array.isArray(order.mergedOps) &&
              order.mergedOps.map((op) => (
                <Tag key={op} color="purple">
                  + {op}
                </Tag>
              ))}
          </Space>
        </Space>
      </List.Item>
    );
  };

  // Groups API
  const loadGroups = async () => {
    try {
      setGroupsLoading(true);
      const res = await fetch(`${API_BASE}/unf_new/hs/exchange?model=EmployeesGroupList`);
      if (!res.ok) throw new Error("Помилка при завантаженні груп");
      const data = await res.json();
      setGroups(Array.isArray(data) ? data : []);
      setGroupsLoadedOnce(true);
    } catch (e) {
      toast.error("Не вдалося завантажити групи: " + e.message);
    } finally {
      setGroupsLoading(false);
    }
  };
  const ensureGroupsLoaded = async () => {
    if (!groupsLoadedOnce) await loadGroups();
  };

  // CRUD
  const addGroup = async (name, employeeIds) => {
    try {
      setGroupsCrudLoading(true);
      const payload = {
        name,
        employees: employeeIds.map((id) => ({
          guid: id,
          name: empById[String(id)]?.name || ""
        }))
      };
      const res = await fetch(`${API_BASE}/unf_new/hs/exchange?model=AddEmployeesGroup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error("Помилка при створенні групи");
      const data = await res.json();
      toast.success("Групу створено");
      await loadGroups();
      setActiveGroupGuid(data?.guid || null);
    } catch (e) {
      toast.error("Не вдалося створити групу: " + e.message);
    } finally {
      setGroupsCrudLoading(false);
    }
  };
  const updateGroup = async (guid, name, employeeIds) => {
    try {
      setGroupsCrudLoading(true);
      const payload = {
        guid,
        name,
        employees: employeeIds.map((id) => ({
          guid: id,
          name: empById[String(id)]?.name || ""
        }))
      };
      const res = await fetch(`${API_BASE}/unf_new/hs/exchange?model=UpdateEmployeesGroup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error("Помилка при оновленні групи");
      toast.success("Групу оновлено");
      await loadGroups();
      setActiveGroupGuid(guid);
    } catch (e) {
      toast.error("Не вдалося оновити групу: " + e.message);
    } finally {
      setGroupsCrudLoading(false);
    }
  };
  const deleteGroup = async (guid) => {
    try {
      setGroupsCrudLoading(true);
      const res = await fetch(`${API_BASE}/unf_new/hs/exchange?model=DelEmployeesGroup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ guid })
      });
      if (!res.ok) throw new Error("Помилка при видаленні групи");
      toast.success("Групу видалено");
      await loadGroups();
      setActiveGroupGuid(null);
      setGroupForm({ guid: null, name: "", employeeIds: [] });
      if (groupSelectGuid === guid) setGroupSelectGuid(null);
    } catch (e) {
      toast.error("Не вдалося видалити групу: " + e.message);
    } finally {
      setGroupsCrudLoading(false);
    }
  };

  // Groups UI helpers
  const openGroupsModal = async () => {
    setGroupsModalOpen(true);
    await loadGroups();
    setActiveGroupGuid(null);
    setGroupForm({ guid: null, name: "", employeeIds: [] });
  };
  const pickGroupToForm = (g) => {
    setActiveGroupGuid(g?.guid || null);
    // guid из группы -> оставляем только тех, кто реально есть у станка
    const ids = (g?.employees || [])
      .map((x) => x.guid)
      .filter((guid) => machineEmployees.some((me) => sameEmployee(me, guid)));
    setGroupForm({ guid: g?.guid || null, name: g?.name || "", employeeIds: ids });
  };
  const startCreateNewGroup = () => {
    setActiveGroupGuid(null);
    setGroupForm({ guid: null, name: "", employeeIds: [] });
  };

  // Apply group to plan
  const applyGroupByGuid = async (mode /* 'replace' | 'append' */) => {
    if (!groupSelectGuid) {
      toast.warn("Оберіть групу для застосування");
      return;
    }
    await ensureGroupsLoaded();
    const g = groups.find((x) => x.guid === groupSelectGuid);
    if (!g) {
      toast.error("Групу не знайдено");
      return;
    }
    const members = (g.employees || [])
      .map((x) => x.guid)
      .filter((guid) => machineEmployees.some((e) => sameEmployee(e, guid)));

    if (members.length === 0) {
      toast.warn("У цій групі немає співробітників для обраного верстата");
      return;
    }

    if (mode === "replace") {
      updatePlannedEmployees(members);
      toast.success("Групу застосовано (замінено)");
    } else {
      const currentKeys = new Set(selectedEmpsIds.map((x) => String(x)));
      const merged = [...selectedEmpsIds];
      members.forEach((m) => {
        const k = String(m);
        if (!currentKeys.has(k)) {
          currentKeys.add(k);
          merged.push(m);
        }
      });
      updatePlannedEmployees(merged);
      toast.success("Групу додано до вибраних");
    }
  };

  // UI
  return (
    <Layout style={{ height: "100vh", background: "linear-gradient(135deg, #f7faff 0%, #ffffff 60%)" }}>
      <style>{`
        .orders-fill-tabs { display: flex; flex-direction: column; }
        .orders-fill-tabs .ant-tabs-content-holder { display: flex; min-height: 0; flex: 1; }
        .orders-fill-tabs .ant-tabs-content { height: 100%; }
        .orders-fill-tabs .ant-tabs-tabpane { height: 100%; }
      `}</style>
      <Header style={{ color: "white", fontSize: 18, background: "#1f2937", letterSpacing: 0.3 }}>
        Планування змін (дата → верстат → співробітники{showOrdersSelect ? " + наряди" : ""})
      </Header>

      <Content
        style={{
          padding: 12,
          height: "calc(100% - 64px)",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          minHeight: 0,
          overflow: "hidden"
        }}
      >
        <Space wrap align="start">
          <DatePicker
            allowClear={false}
            value={selectedDate}
            onChange={setSelectedDate}
            disabledDate={(current) => current && current.isBefore(dayjs().startOf("day"))}
          />

          <Select
            placeholder="Виберіть верстат"
            onChange={(val) => {
              setSelectedMachine(val);
              setGroupSelectGuid(null);
            }}
            style={{ minWidth: 320 }}
            value={selectedMachine}
            showSearch
            optionFilterProp="children"
          >
            {machines.map((m) => (
              <Option key={m.id} value={m.id}>
                {m.name}
              </Option>
            ))}
          </Select>

          <Button icon={<TeamOutlined />} onClick={openGroupsModal} disabled={!selectedMachine}>
            Групи співробітників
          </Button>

          <Button icon={<SaveOutlined />} onClick={() => toast.success("План успішно збережено")} disabled={!selectedMachine}>
            Зберегти
          </Button>

          <Popconfirm
            title="Очистити список співробітників на цю дату і верстат?"
            okText="Так"
            cancelText="Ні"
            onConfirm={clearAllEmployees}
            disabled={!selectedEmpsIds.length}
          >
            <Button icon={<ClearOutlined />} danger disabled={!selectedEmpsIds.length}>
              Очистити співробітників
            </Button>
          </Popconfirm>

        </Space>

        {/* EMPLOYEES ROW */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            flex: 1,
            minHeight: 0,
            overflow: "hidden"
          }}
        >
          <Card
            title="Доступні співробітники"
            size="small"
            bodyStyle={{
              paddingTop: 8,
              paddingBottom: 8,
              display: "flex",
              flexDirection: "column",
              minHeight: 0
            }}
            style={{ overflow: "hidden", minHeight: 0 }}
          >
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 4 }}>
              <List
                dataSource={machineEmployees}
                locale={{ emptyText: <Empty description="Немає співробітників" /> }}
                renderItem={(emp) => {
                  const selected = selectedEmpsIds.map(toKey).includes(toKey(emp.id));
                  return (
                    <List.Item
                      actions={[
                        selected ? (
                          <Button key="remove" size="small" danger icon={<UserDeleteOutlined />} onClick={() => removeEmployee(emp.id)}>
                            Прибрати
                          </Button>
                        ) : (
                          <Button key="add" size="small" type="primary" ghost icon={<UserAddOutlined />} onClick={() => addEmployee(emp.id)}>
                            Додати
                          </Button>
                        )
                      ]}
                      style={{
                        paddingTop: 8,
                        paddingBottom: 8,
                        background: selected ? "#f6ffed" : "transparent",
                        borderRadius: 8
                      }}
                    >
                      <Space>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            background: "#36cfc9",
                            display: "inline-block",
                            borderRadius: 999
                          }}
                        />
                        <span style={{ fontWeight: 500 }}>{emp.name}</span>
                        {typeof emp.maxMinutes === "number" && <Tag>{emp.maxMinutes} хв/день</Tag>}
                      </Space>
                    </List.Item>
                  );
                }}
              />
            </div>
          </Card>

          <Card
            title="Заплановані на зміну"
            extra={
              <Space wrap align="center">
                <Select
                  placeholder="Група співробітників"
                  value={groupSelectGuid}
                  onChange={setGroupSelectGuid}
                  style={{ minWidth: 220 }}
                  onDropdownVisibleChange={(open) => {
                    if (open) ensureGroupsLoaded();
                  }}
                  loading={groupsLoading}
                  allowClear
                >
                  {groups.map((g) => (
                    <Option key={g.guid} value={g.guid}>
                      {g.name}
                    </Option>
                  ))}
                </Select>
                <Button size="small" type="primary" onClick={() => applyGroupByGuid("replace")} disabled={!groupSelectGuid || !selectedMachine}>
                  Заповнити
                </Button>
                <Button size="small" onClick={() => applyGroupByGuid("append")} disabled={!groupSelectGuid || !selectedMachine}>
                  Додати
                </Button>

                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  Співробітників:&nbsp;
                  <Tag color="geekblue" style={{ marginLeft: 4 }}>
                    {selectedEmpsIds.length}
                  </Tag>
                </span>
              </Space>
            }
            size="small"
            bodyStyle={{
              paddingTop: 8,
              paddingBottom: 8,
              display: "flex",
              flexDirection: "column",
              minHeight: 0
            }}
            style={{ overflow: "hidden", minHeight: 0 }}
          >
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 4 }}>
              <List
                dataSource={selectedEmpsIds}
                locale={{ emptyText: <Empty description="Нікого не вибрано" /> }}
                renderItem={(empId) => {
                  const emp = empById[String(empId)];
                  return (
                    <List.Item
                      actions={[
                        <Button key="del" size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeEmployee(empId)} />
                      ]}
                      style={{
                        paddingTop: 8,
                        paddingBottom: 8,
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                        background: "#ffffff"
                      }}
                    >
                      <Space>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            background: "#597ef7",
                            display: "inline-block",
                            borderRadius: 999
                          }}
                        />
                        <strong>{emp?.name || "—"}</strong>
                        {typeof emp?.maxMinutes === "number" && <Tag color="blue">{emp.maxMinutes} хв</Tag>}
                      </Space>
                    </List.Item>
                  );
                }}
              />
            </div>
          </Card>
        </div>

        {/* ORDERS ROW */}
        {showOrdersSelect && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              flex: 1,
              minHeight: 0,
              overflow: "hidden"
            }}
          >
            <Card
              title="Наряди"
              size="small"
              bodyStyle={{ paddingTop: 8, paddingBottom: 8, display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}
              style={{ overflow: "hidden", minHeight: 0, height: "100%" }}
            >
              <Tabs
                activeKey={ordersTab}
                onChange={setOrdersTab}
                size="small"
                className="orders-fill-tabs"
                style={{ height: "100%" }}
                items={[
                  {
                    key: "available",
                    label: "Доступні наряди",
                    children: (
                      <div style={{ height: "100%", overflowY: "auto", paddingRight: 12, paddingBottom: 24, boxSizing: "border-box" }}>
                        <List
                          dataSource={machineOrders}
                          locale={{ emptyText: <Empty description="Немає нарядів" /> }}
                          renderItem={(order) => renderOrderItem(order)}
                        />
                      </div>
                    )
                  },
                  {
                    key: "completed",
                    label: "Виконані наряди",
                    children: (
                      <div style={{ height: "100%", overflowY: "auto", paddingRight: 12, paddingBottom: 24, boxSizing: "border-box" }}>
                        <List
                          dataSource={completedOrders}
                          locale={{ emptyText: <Empty description="Немає виконаних нарядів" /> }}
                          renderItem={(order) => renderOrderItem(order)}
                        />
                      </div>
                    )
                  }
                ]}
              />
            </Card>

            <Card
              title="Заплановані наряди"
              extra={
                <Space wrap>
                  <Tag color={availableMinutes < 0 ? "red" : "cyan"}>
                    Доступно: {availableMinutes} хв
                  </Tag>
                  <Tag color="blue">Заплановано: {sumPlanned} хв</Tag>
                </Space>
              }
              size="small"
              bodyStyle={{ paddingTop: 8, paddingBottom: 8, display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}
              style={{ overflow: "hidden", minHeight: 0, height: "100%" }}
            >
              <div style={{ flex: 1, minHeight: 0, paddingRight: 10, paddingBottom: 10, boxSizing: "border-box" }}>
                <div style={{ height: "100%", overflowY: "auto", paddingRight: 12, paddingBottom: 24, boxSizing: "border-box" }}>
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="plannedOrders">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          <List
                            dataSource={plannedList}
                            locale={{ emptyText: <Empty description="Нічого не заплановано" /> }}
                            renderItem={(order, index) => (
                              <Draggable key={String(order.id)} draggableId={String(order.id)} index={index}>
                                {(drag) => (
                                  <List.Item
                                    ref={drag.innerRef}
                                    {...drag.draggableProps}
                                    {...drag.dragHandleProps}
                                    style={{
                                      ...drag.draggableProps.style,
                                      borderRadius: 8,
                                      border: "1px solid #f0f0f0",
                                      background: "#ffffff",
                                      marginBottom: index === plannedList.length - 1 ? 0 : 8,
                                      paddingTop: 8,
                                      paddingBottom: 8
                                    }}
                                    actions={[
                                      <Button key="removeOrder" type="link" danger onClick={() => handleRemoveOrder(order.id)}>
                                        Видалити
                                      </Button>
                                    ]}
                                  >
                                    <Space direction="vertical" size={2}>
                                      <span
                                        style={{
                                          color: order.done ? "#8c8c8c" : "inherit",
                                          textDecoration: order.done ? "line-through" : "none"
                                        }}
                                      >
                                        {order.done ? "\u2714 " : ""}
                                        {index + 1}. {order.name} — {order.duration} хв
                                      </span>
                                      <Space size={4} wrap>
                                        {order.done && <Tag color="green">Виконано, не рахується в плані</Tag>}
                                        {order.carried && <Tag color="volcano">Перенесено з попереднього дня</Tag>}
                                        {!order.done && order.factTime > 0 && (
                                          <Tag color="gold">
                                            факт {order.factTime} з {order.normTime} хв
                                          </Tag>
                                        )}
                                        {Array.isArray(order.mergedOps) &&
                                          order.mergedOps.map((op) => (
                                            <Tag key={op} color="purple">
                                              + {op}
                                            </Tag>
                                          ))}
                                      </Space>
                                    </Space>
                                  </List.Item>
                                )}
                              </Draggable>
                            )}
                          />
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              </div>
            </Card>
          </div>
        )}

        <ToastContainer position="bottom-right" />

        {/* MODAL: Groups (CRUD only) */}
        <Modal
          title="Групи співробітників"
          open={groupsModalOpen}
          onCancel={() => setGroupsModalOpen(false)}
          footer={null}
          width={960}
          destroyOnClose
        >
          <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: 12 }}>
            <Card
              size="small"
              title="Список груп"
              extra={
                <Button icon={<PlusOutlined />} size="small" onClick={startCreateNewGroup}>
                  Нова група
                </Button>
              }
              bodyStyle={{ paddingTop: 8, paddingBottom: 8 }}
            >
              <Search
                placeholder="Пошук по назві"
                onChange={(e) => setGroupSearch(e.target.value)}
                style={{ marginBottom: 8 }}
                allowClear
              />
              <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
                <List
                  loading={groupsLoading}
                  dataSource={groups.filter((g) => g.name?.toLowerCase().includes(groupSearch.toLowerCase()))}
                  locale={{ emptyText: <Empty description="Немає груп" /> }}
                  renderItem={(g) => {
                    const active = activeGroupGuid === g.guid;
                    return (
                      <List.Item
                        onClick={() => pickGroupToForm(g)}
                        style={{ cursor: "pointer", borderRadius: 8, background: active ? "#f0f5ff" : "transparent" }}
                        actions={[
                          <Button
                            key="edit"
                            type="link"
                            icon={<EditOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              pickGroupToForm(g);
                            }}
                          >
                            Редагувати
                          </Button>
                        ]}
                      >
                        <Space direction="vertical" size={0}>
                          <strong>{g.name}</strong>
                          <span style={{ color: "#888", fontSize: 12 }}>{g.employees?.length || 0} співр.</span>
                        </Space>
                      </List.Item>
                    );
                  }}
                />
              </div>
              <Divider style={{ margin: "8px 0" }} />
              <Space>
                <Button onClick={loadGroups}>Оновити</Button>
                {activeGroupGuid && (
                  <Popconfirm title="Видалити цю групу?" okText="Так" cancelText="Ні" onConfirm={() => deleteGroup(activeGroupGuid)}>
                    <Button danger icon={<DeleteOutlined />} loading={groupsCrudLoading}>
                      Видалити
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </Card>

            <Card size="small" title="Форма групи" bodyStyle={{ paddingTop: 8, paddingBottom: 8 }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input
                  placeholder="Назва групи"
                  value={groupForm.name}
                  onChange={(e) => setGroupForm((s) => ({ ...s, name: e.target.value }))}
                />
                <div>
                  <div style={{ marginBottom: 6, fontWeight: 500 }}>Співробітники групи</div>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Оберіть співробітників"
                    value={groupForm.employeeIds}
                    onChange={(vals) => setGroupForm((s) => ({ ...s, employeeIds: vals }))}
                    showSearch
                    optionFilterProp="children"
                  >
                    {machineEmployees.map((e) => (
                      <Option key={e.id} value={e.id}>
                        {e.name}
                      </Option>
                    ))}
                  </Select>
                </div>
                <Space>
                  {!groupForm.guid ? (
                    <Button
                      type="primary"
                      onClick={() => {
                        if (!groupForm.name.trim()) {
                          toast.warn("Вкажіть назву групи");
                          return;
                        }
                        addGroup(groupForm.name.trim(), groupForm.employeeIds);
                      }}
                      loading={groupsCrudLoading}
                    >
                      Створити групу
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      onClick={() => {
                        if (!groupForm.name.trim()) {
                          toast.warn("Вкажіть назву групи");
                          return;
                        }
                        updateGroup(groupForm.guid, groupForm.name.trim(), groupForm.employeeIds);
                      }}
                      loading={groupsCrudLoading}
                    >
                      Зберегти зміни
                    </Button>
                  )}
                  <Button onClick={() => setGroupsModalOpen(false)}>Закрити</Button>
                </Space>
              </Space>
            </Card>
          </div>
        </Modal>
      </Content>
    </Layout>
  );
}